/**
 * Toast — lightweight, dependency-free transient notifications for the storefront.
 *
 * A single bottom-anchored stack rendered above the app. Used for quick feedback such
 * as "Добавлено в корзину". Auto-dismisses; styled with ``--ms-*`` tokens.
 *
 * Local to the main app (the shared-ui kit has no toast and is shared with the panel).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import styles from "./ToastProvider.module.css";

export type ToastTone = "default" | "success";

interface ToastItem {
  readonly id: number;
  readonly text: string;
  readonly tone: ToastTone;
}

export interface ToastApi {
  /** Show a transient toast. Defaults to the "success" tone and ~2s duration. */
  readonly show: (text: string, tone?: ToastTone, durationMs?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION_MS = 2000;

export interface ToastProviderProps {
  readonly children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastItem[]>([]);
  const nextId = useRef(0);

  const show = useCallback<ToastApi["show"]>(
    (text, tone = "success", durationMs = DEFAULT_DURATION_MS) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, text, tone }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, durationMs);
    },
    [],
  );

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.stack} aria-live="polite" role="status">
        {toasts.map((toast) => (
          <div key={toast.id} className={styles.toast} data-tone={toast.tone}>
            {toast.tone === "success" ? (
              <svg
                className={styles.icon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m5 13 4 4L19 7" />
              </svg>
            ) : null}
            <span>{toast.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Access the toast API. Must be used within {@link ToastProvider}. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}
